import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import type { SortingState } from '@tanstack/react-table';
import { UsersIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AssetUserType } from '@/common/asset';
import { ROLE } from '@/common/role';
import { AbsoluteLoader } from '@/components/absoluteLoader';
import { BulkActionBar } from '@/components/bulk/BulkActionBar';
import { toSortParam } from '@/components/data-table/sort-param';
import { Restricted } from '@/components/Restricted';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { buttonVariants } from '@/components/ui/button';
import { useBulkSelection } from '@/hooks/use-bulk-selection';
import { usePermission } from '@/hooks/use-permission';
import { reportBulkDelete, runBulkDelete } from '@/lib/utils/bulk-delete';
import { deleteAssetUser, useMutationRemoveAssetUser } from '@/queries/assets/users/delete-asset-user';
import { useQueryListAssetUsersOptions } from '@/queries/assets/users/list-asset-users';

import { AddAssetUser } from './AddAssetUser';
import { AssetUserList } from './AssetUserList';
import { ASSET_USER_SORT_FIELDS, getAssetUserDisplayName } from './columns';

export type AssetUsersListContainerProps = {
	assetId: string;
	organizationId: string;
};

export function AssetUsersListContainer({ assetId, organizationId }: AssetUsersListContainerProps) {
	const { t } = useTranslation();
	const [sorting, setSorting] = useState<SortingState>([]);
	const {
		isLoading,
		data: assetUsers,
		refetch,
		isRefetching,
	} = useQuery({
		...useQueryListAssetUsersOptions(assetId, { sort: toSortParam(sorting, ASSET_USER_SORT_FIELDS) }),
		// Keep the current rows on screen while a new sort order loads.
		placeholderData: keepPreviousData,
	});
	const removeUserApi = useMutation(useMutationRemoveAssetUser());

	// Multi-select + bulk remove
	const canDeleteUsers = usePermission(ROLE.ASSET_USERS_DELETE);
	const userIds = useMemo(() => (assetUsers ?? []).map((assetUser) => assetUser.id as string), [assetUsers]);
	const selection = useBulkSelection(userIds);
	const [isBulkDeleting, setIsBulkDeleting] = useState(false);
	const userLabel = {
		singular: t('common.entities.user', { count: 1 }),
		plural: t('common.entities.user', { count: 2 }),
	};
	const handleBulkDelete = async () => {
		setIsBulkDeleting(true);
		try {
			const result = await runBulkDelete(selection.selectedIds, (id) => deleteAssetUser({ assetId, id }));
			reportBulkDelete(result, userLabel);
			selection.clear();
			await refetch?.();
		} finally {
			setIsBulkDeleting(false);
		}
	};

	// Removing a user is permanent, so it is confirmed first
	const [userToRemove, setUserToRemove] = useState<AssetUserType | null>(null);
	const onDeleteUser = async (id: string) => {
		setUserToRemove(assetUsers?.find((u) => u.id === id) ?? null);
	};
	const removeUser = async (user: AssetUserType) => {
		await removeUserApi.mutateAsync({
			assetId: user.assetId as string,
			id: user.id as string,
		});
		refetch?.();
	};

	if (isLoading) {
		return <div>{t('assets.users.loading')}</div>;
	}

	return (
		<div className="flex-1">
			<div className="space-y-4">
				<div className="flex justify-between items-center">
					<h3 className="text-sm font-semibold uppercase tracking-wider">{t('assets.users.assignedUsers')}</h3>
					<Restricted to={ROLE.ASSET_USERS_CREATE}>
						<div className="flex gap-2">
							<AddAssetUser
								assetId={assetId}
								refetch={refetch}
								assetUsers={assetUsers}
								organizationId={organizationId}
							/>
						</div>
					</Restricted>
				</div>
				{canDeleteUsers && selection.count > 0 && (
					<BulkActionBar
						count={selection.count}
						total={userIds.length}
						label={userLabel}
						isDeleting={isBulkDeleting}
						onClear={selection.clear}
						onDelete={handleBulkDelete}
						description={t('assets.users.removeDescription')}
					/>
				)}
				<div className="relative">
					<AbsoluteLoader isLoading={isLoading || isRefetching || removeUserApi.isPending || isBulkDeleting} />
					{!assetUsers?.length && (
						<div className="text-center py-12 border border-dashed rounded-lg">
							<UsersIcon className="size-8 mx-auto mb-2 opacity-50" />
							<p>{t('assets.users.empty')}</p>
						</div>
					)}
					<AssetUserList
						assetUsers={assetUsers}
						onDeleteUser={onDeleteUser}
						sorting={sorting}
						onSortingChange={setSorting}
						selection={canDeleteUsers ? selection : undefined}
					/>
				</div>
				<AlertDialog open={!!userToRemove} onOpenChange={(open) => !open && setUserToRemove(null)}>
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>
								{t('assets.users.removeTitle', {
									name:
										(userToRemove && getAssetUserDisplayName(userToRemove)) ?? t('common.entities.user', { count: 1 }),
								})}
							</AlertDialogTitle>
							<AlertDialogDescription>{t('assets.users.removeDescription')}</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel>{t('common.actions.cancel')}</AlertDialogCancel>
							<AlertDialogAction
								className={buttonVariants({ variant: 'destructive' })}
								onClick={() => userToRemove && void removeUser(userToRemove)}
							>
								{t('assets.users.removeUser')}
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
			</div>
		</div>
	);
}
